import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, ActivityIndicator } from 'react-native';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { DateTime } from 'luxon';
import Toast from 'react-native-toast-message';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import {
    createPayinConfig,
    getPaymentUrl,
    createRecurringGift,
    pollPayinStatus,
    calculateGivingCharge,
    getGivingFeeNotice,
} from '@/data/giving';
import GivingHero from '@/components/Giving/GivingHero';
import GivingCard from '@/components/Giving/GivingCard';
import FundSplitsEditor from '@/components/Giving/FundSplitsEditor';
import NoteInput from '@/components/Giving/NoteInput';
import PaymentMethodPicker from '@/components/Giving/PaymentMethodPicker';
import RecurringGiftToggle from '@/components/Giving/RecurringGiftToggle';
import PaymentWebView from '@/components/Giving/PaymentWebView';
import GivingReceipt from '@/components/Giving/GivingReceipt';
import ManagePaymentMethodsModal from '@/components/Giving/ManagePaymentMethodsModal';
import AddPaymentMethodModal from '@/components/Giving/AddPaymentMethodModal';
import ManageRecurringGiftsModal from '@/components/Giving/ManageRecurringGiftsModal';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import { PatternBackground } from '@/components/General/Backgrounds';
import FeeCoverageToggle from '@/components/Giving/FeeCoverageToggle';
import { isPaymentMethodUsable, preferredPaymentMethodID } from '@/utils/paymentMethodStatus';

export default function GiveScreen() {
    const queryClient = useQueryClient();
    const { theme: { colors, scheme } } = useUnistyles();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const organizationID = loggedInUser?.organizationID;
    const personID = loggedInUser?.id;

    const { data: organization } = Query.organization.byId({ id: organizationID });
    const { data: givingMerchant } = Query.giving.merchant({ organizationID });
    const { data: paymentMethods } = Query.giving.paymentMethods({ organizationID, personID });
    const { data: funds } = Query.contributionFunds.all();

    const [amountCents, setAmountCents] = useState(0);
    const [fundSplits, setFundSplits] = useState([]);
    const [note, setNote] = useState('');
    const [selectedPaymentMethodID, setSelectedPaymentMethodID] = useState(null);
    const [recurring, setRecurring] = useState({ isRecurring: false, frequency: 'MONTHLY', startDate: new Date() });
    const [paymentUrl, setPaymentUrl] = useState(null);
    const [payinConfigID, setPayinConfigID] = useState(null);
    const [paymentError, setPaymentError] = useState(null);
    const [recurringSaveError, setRecurringSaveError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [paymentTimedOut, setPaymentTimedOut] = useState(false);
    const [receiptData, setReceiptData] = useState(null);
    const [showManageMethods, setShowManageMethods] = useState(false);
    const [showAddMethod, setShowAddMethod] = useState(false);
    const [showRecurringModal, setShowRecurringModal] = useState(false);
    const [chargeSummary, setChargeSummary] = useState(null);
    const [coverFees, setCoverFees] = useState(true);
    const [reviewedCharge, setReviewedCharge] = useState(null);
    const effectiveCoverFees = !organization?.allowGiversToChooseCoverFees || coverFees;

    useEffect(() => {
        if (!paymentMethods) return;
        const nextID = preferredPaymentMethodID(paymentMethods, selectedPaymentMethodID);
        if (nextID !== selectedPaymentMethodID) {
            setSelectedPaymentMethodID(nextID);
            setReviewedCharge(null);
        }
    }, [paymentMethods, selectedPaymentMethodID]);

    useEffect(() => {
        if (!funds?.length || fundSplits.length > 0) return;
        const defaultFund = funds.find((f) => f.isDefault) || funds[0];
        setFundSplits([{ fundID: defaultFund?.id, amount: amountCents }]);
    }, [funds, fundSplits.length, amountCents]);

    const handleAmountChange = useCallback((cents) => {
        setAmountCents(cents);
        setReviewedCharge(null);
        setFundSplits((prev) => (prev.length === 1 ? [{ ...prev[0], amount: cents }] : prev));
    }, []);

    const totalSplitCents = useMemo(() => fundSplits.reduce((sum, row) => sum + (row.amount || 0), 0), [fundSplits]);
    const hasAmount = amountCents > 0;
    const isBalanced = totalSplitCents === amountCents && hasAmount;

    const billingContact = useMemo(() => {
        if (!loggedInUser) return {};
        const name = `${loggedInUser.firstName || ''} ${loggedInUser.lastName || ''}`.trim();
        return { name, email: loggedInUser.email || '' };
    }, [loggedInUser]);

    const selectedPaymentMethod = useMemo(() =>
        paymentMethods?.find((method) => (method.paymentMethodID || method.id) === selectedPaymentMethodID) || null,
    [paymentMethods, selectedPaymentMethodID]);
    const feeSummary = givingMerchant?.billingProfile || null;
    const givingBaseColor = givingMerchant?.appearance?.baseColor || colors.giving;
    const localCharge = useMemo(() =>
        calculateGivingCharge(amountCents, selectedPaymentMethod, feeSummary, effectiveCoverFees),
    [amountCents, selectedPaymentMethod, feeSummary, effectiveCoverFees]);
    const calculatedCharge = isPaymentMethodUsable(selectedPaymentMethod) ? reviewedCharge || localCharge : null;
    const feeNotice = useMemo(() =>
        effectiveCoverFees ? getGivingFeeNotice(selectedPaymentMethod, feeSummary, organization?.name, organization?.allowGiversToChooseCoverFees) : null,
    [selectedPaymentMethod, feeSummary, organization?.name, organization?.allowGiversToChooseCoverFees, effectiveCoverFees]);

    const createPayinMutation = useMutation({
        mutationFn: (payload) => createPayinConfig(payload),
    });

    const createRecurringMutation = useMutation({
        mutationFn: (payload) => createRecurringGift(payload),
        onError: (error) => {
            const message = error?.message || 'Failed to schedule recurring gift';
            setRecurringSaveError(message);
            Toast.show({ type: 'error', text2: message });
        },
    });

    // Give with a saved payment method: create the payin config on press and
    // show the autoSubmit payment screen.
    const handleGive = useCallback(async () => {
        setPaymentError(null);
        if (!isBalanced || !organizationID || !selectedPaymentMethodID || !calculatedCharge) return;

        const payload = {
            organizationID,
            amount: amountCents,
            fundSplits,
            note,
            personID,
            paymentMethodID: selectedPaymentMethodID,
            isRecurring: recurring.isRecurring,
            coverFees: effectiveCoverFees,
            expectedGrossAmount: calculatedCharge.grossAmount,
        };

        try {
            const config = await createPayinMutation.mutateAsync(payload);
            if (config.confirmationRequired) {
                setReviewedCharge(config);
                setCoverFees(config.coverFees);
                queryClient.setQueryData(QueryKeys.organization.byId(organizationID), (current) => current ? { ...current, allowGiversToChooseCoverFees: config.allowGiversToChooseCoverFees } : current);
                Toast.show({ type: 'info', text2: 'Payment total changed. Review the total and press Give to confirm.' });
                return;
            }
            if (!Number.isInteger(config.grossAmount) || !Number.isInteger(config.convenienceFee)) {
                throw new Error('Payment fee details were unavailable');
            }
            setChargeSummary({
                grossAmount: config.grossAmount,
                convenienceFee: config.convenienceFee,
                methodType: config.methodType,
                coverFees: config.coverFees,
            });
            setPayinConfigID(config.payinConfigID);
            const url = getPaymentUrl({
                sessionKey: config.sessionKey,
                payinConfigID: config.payinConfigID,
                paymentMethodID: selectedPaymentMethodID,
                autoSubmit: !!selectedPaymentMethodID,
                theme: scheme === 'dark' ? 'dark' : 'light',
                bg: colors.background,
            });
            setPaymentUrl(url);
        } catch (error) {
            Toast.show({ type: 'error', text2: error?.message || 'Failed to start payment' });
        }
    }, [isBalanced, organizationID, amountCents, fundSplits, note, personID, selectedPaymentMethodID, calculatedCharge, effectiveCoverFees, recurring.isRecurring, scheme, colors.background, createPayinMutation, queryClient]);

    const handleSelectPaymentMethod = useCallback((paymentMethodID) => {
        setSelectedPaymentMethodID(paymentMethodID);
        setReviewedCharge(null);
        setPaymentError(null);
    }, []);

    const handlePaymentMethodAdded = useCallback((method) => {
        setSelectedPaymentMethodID(method.paymentMethodID);
        setReviewedCharge(null);
    }, []);

    // Poll GivingPayins for the payin record once the payment form is shown.
    // This is more reliable than postMessage from the WebView.
    useEffect(() => {
        if (!paymentUrl || !payinConfigID) return;
        let cancelled = false;

        setProcessing(true);

        const poll = async () => {
            try {
                const payin = await pollPayinStatus(payinConfigID, { intervalMs: 2000, timeoutMs: 120000 });
                if (cancelled) return;
                if (payin) {
                    handlePayinFound(payin);
                } else {
                    // Timeout — don't auto-fail, the payment may still be
                    // processing server-side. Swap the dead payment view for a
                    // "still processing" notice instead of leaving it stuck.
                    console.warn('[give] poll timeout — payin not found within 120s');
                    setPaymentUrl(null);
                    setPayinConfigID(null);
                    setProcessing(false);
                    setPaymentTimedOut(true);
                }
            } catch (err) {
                if (cancelled) return;
                console.error('[give] poll error:', err);
                setPaymentUrl(null);
                setPayinConfigID(null);
                setProcessing(false);
                setPaymentTimedOut(true);
            }
        };
        poll();

        return () => { cancelled = true; };
    }, [paymentUrl, payinConfigID]);

    const handlePayinFound = useCallback(async (payin) => {
        const raw = payin.rawPayload || {};
        const card = payin.card || raw.card || raw.payment_method?.card || null;
        const ach = payin.ach || raw.ach || raw.payment_method?.ach || null;
        const applePay = payin.applePay || raw.apple_pay || raw.payment_method?.apple_pay || null;
        const paymentMethodID = payin.paymentMethodID || selectedPaymentMethodID;

        const fundSplitsForReceipt = fundSplits.map((s) => {
            const fund = funds?.find((f) => f.id === s.fundID);
            return {
                fundID: s.fundID,
                fundName: fund?.name || 'General',
                amountCents: s.amount,
            };
        });

        setReceiptData({
            payinID: payin.payinID || 'N/A',
            amountCents,
            convenienceFeeCents: chargeSummary?.convenienceFee,
            grossAmountCents: chargeSummary?.grossAmount,
            currencyCode: feeSummary?.currencyCode || 'USD',
            methodType: chargeSummary?.methodType || payin.methodType || selectedPaymentMethod?.methodType,
            fundSplits: fundSplitsForReceipt,
            note,
            billingContact,
            card: card || (selectedPaymentMethod?.cardBrand ? { brand: selectedPaymentMethod.cardBrand, last_4: selectedPaymentMethod.cardLast4 } : null),
            ach: ach || (selectedPaymentMethod?.achAccountNumberLast4 ? { bank_name: selectedPaymentMethod.achBankName, account_number_last_4: selectedPaymentMethod.achAccountNumberLast4 } : null),
            applePay: applePay || ((selectedPaymentMethod?.methodType || '').toUpperCase() === 'APPLE_PAY'
                ? { description: [selectedPaymentMethod.cardBrand, selectedPaymentMethod.cardLast4].filter(Boolean).join(' ') || null }
                : null),
            date: payin.createdAt || raw.created_at || new Date().toISOString(),
        });

        if (recurring.isRecurring) {
            try {
                await createRecurringMutation.mutateAsync({
                    organizationID,
                    paymentMethodID,
                    amount: amountCents,
                    fundSplits,
                    note,
                    frequency: recurring.frequency,
                    coverFees: chargeSummary?.coverFees ?? effectiveCoverFees,
                    initialPayinConfigID: payinConfigID,
                    startDate: DateTime.fromJSDate(recurring.startDate).toISODate(),
                });
            } catch {
                // Toast shown by mutation onError.
            }
        }

        queryClient.invalidateQueries({ queryKey: QueryKeys.giving.paymentMethods(organizationID, personID) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.giving.recurringGifts(organizationID, personID) });
        setPaymentUrl(null);
        setPayinConfigID(null);
        setProcessing(false);
        setSuccess(true);
    }, [recurring, organizationID, amountCents, fundSplits, note, selectedPaymentMethodID, selectedPaymentMethod, chargeSummary, feeSummary, personID, effectiveCoverFees, payinConfigID, queryClient, createRecurringMutation, funds, billingContact]);

    const handlePaymentError = useCallback((message) => {
        setPaymentUrl(null);
        setPayinConfigID(null);
        setProcessing(false);
        setPaymentError(message || 'Payment failed');
    }, []);

    const resetFlow = useCallback(() => {
        setSuccess(false);
        setPaymentTimedOut(false);
        setPaymentError(null);
        setRecurringSaveError(null);
        setChargeSummary(null);
        setPaymentUrl(null);
        setPayinConfigID(null);
        setProcessing(false);
        setAmountCents(0);
        setNote('');
        const defaultFund = funds?.find((f) => f.isDefault) || funds?.[0];
        setFundSplits([{ fundID: defaultFund?.id, amount: 0 }]);
        setSelectedPaymentMethodID(null);
        setRecurring({ isRecurring: false, frequency: 'MONTHLY', startDate: new Date() });
    }, [funds]);

    const formatCurrency = (cents) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: feeSummary?.currencyCode || 'USD', minimumFractionDigits: 2 }).format(cents / 100);

    const submitButtonText = useMemo(() => {
        return `Give ${formatCurrency(calculatedCharge?.grossAmount ?? amountCents)}`;
    }, [amountCents, calculatedCharge]);

    const givingDisabledReason = !hasAmount
        ? 'Enter an amount to give.'
        : !isBalanced
            ? 'Fund allocations must equal the gift amount.'
            : !selectedPaymentMethod
                ? 'Add and select a saved payment method before giving.'
                : !feeSummary
                    ? 'Online fee details are unavailable. Try again later.'
                    : !calculatedCharge
                        ? 'Fee details for this payment method are unavailable.'
                        : null;

    // Square logo only — the long/banner variants (logoPublicUrlLong*) are wide
    // and render as an unreadable sliver inside the hero's 48x48 square chip.
    const orgLogoUrl = useMemo(() => {
        if (!organization) return null;
        return scheme === 'light'
            ? (organization.logoPublicUrlDark || organization.logoPublicUrl)
            : (organization.logoPublicUrl || organization.logoPublicUrlDark);
    }, [organization, scheme]);

    const isLoading = !organization || !funds || !givingMerchant || !paymentMethods;
    const isAvailable = organization?.enableGiving && givingMerchant?.merchantStatus === 'ACTIVE';

    if (isLoading) {
        return (
            <View style={[styles.container, styles.center]} testID="give-screen-loading">
                <ActivityIndicator color={colors.giving} />
            </View>
        );
    }

    if (!isAvailable) {
        return (
            <View style={[styles.container, styles.center]} testID="give-screen-unavailable">
                <Unicon name="card-outline" size={48} colorVariant="textTertiary" />
                <Text style={styles.unavailableTitle}>Giving isn&apos;t available for your organization yet.</Text>
            </View>
        );
    }

    if (success) {
        return (
            <View style={styles.container}>
                <PatternBackground color={givingBaseColor} />
                <ScrollView style={styles.scroll} contentContainerStyle={styles.successContent} testID="give-screen-success">
                    <View style={styles.successHeader}>
                        <Unicon name="checkmark-circle" size={64} colorVariant="success" />
                        <Text style={styles.successTitle}>Thank you!</Text>
                        <Text style={styles.successBody}>Your gift has been received.</Text>
                    </View>
                    {recurringSaveError && (
                        <View style={styles.errorCard} testID="give-recurring-save-error">
                            <Text style={styles.errorText}>Your gift was received, but your recurring schedule was not saved. {recurringSaveError}</Text>
                            <Text style={styles.errorText}>Do not give again to retry saving the schedule. Contact your organization for help.</Text>
                        </View>
                    )}
                    {receiptData && (
                        <GivingReceipt
                            receiptData={receiptData}
                            organizationID={organizationID}
                            personEmail={loggedInUser?.email}
                        />
                    )}
                    <View style={styles.successActions}>
                        <SolidButton testID="give-another-button" text="Give again" iconName="heart-outline" type="giving" variant="muted" onPress={resetFlow} />
                        <SolidButton testID="recurring-gifts-link" text="Recurring gifts" variant="muted" type="giving" iconName="repeat" onPress={() => setShowRecurringModal(true)} />
                    </View>
                    <ManageRecurringGiftsModal
                        isOpen={showRecurringModal}
                        onClose={() => setShowRecurringModal(false)}
                        organizationID={organizationID}
                        personID={personID}
                    />
                </ScrollView>
            </View>
        );
    }

    if (paymentTimedOut) {
        return (
            <View style={styles.container}>
                <PatternBackground color={givingBaseColor} />
                <ScrollView style={styles.scroll} contentContainerStyle={styles.successContent} testID="give-payment-timeout">
                    <View style={styles.successHeader}>
                        <Unicon name="time-outline" size={64} colorVariant="textTertiary" />
                        <Text style={styles.successTitle}>Payment not confirmed</Text>
                        <Text style={styles.successBody}>We couldn&apos;t confirm whether your gift was processed. Check the Giving tab on your profile or contact your organization before trying again to avoid a duplicate gift.</Text>
                    </View>
                    <View style={styles.successActions}>
                        <SolidButton testID="give-timeout-done" text="Done" type="giving" variant="muted" onPress={resetFlow} />
                    </View>
                </ScrollView>
            </View>
        );
    }

    if (paymentUrl && selectedPaymentMethodID) {
        return (
            <View style={styles.container}>
                <PatternBackground color={givingBaseColor} />
                <ScrollView style={styles.scroll} contentContainerStyle={styles.paymentContent} testID="give-screen-payment">
                    <Text style={styles.paymentHeaderTitle}>Payment</Text>
                    <Text style={styles.paymentHeaderSubtitle}>Confirming your gift</Text>
                    <View style={styles.paymentWebviewCard}>
                        <PaymentWebView
                            url={paymentUrl}
                            onError={handlePaymentError}
                            style={styles.paymentWebview}
                        />
                        {processing && (
                            <View style={styles.processingOverlay} testID="give-payment-processing">
                                <ActivityIndicator color={colors.giving} size="large" />
                                <Text style={styles.processingText}>Processing your gift...</Text>
                                <Text style={styles.processingSubtext}>This may take a moment</Text>
                            </View>
                        )}
                    </View>
                </ScrollView>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <PatternBackground color={givingBaseColor} />
            <ScrollView style={styles.scroll} contentContainerStyle={styles.content} testID="give-screen">
                <View style={styles.column}>
                    <GivingHero
                        testID="give-hero"
                        orgName={organization?.name || 'Give'}
                        logoUrl={orgLogoUrl}
                        amountCents={amountCents}
                        onAmountChange={handleAmountChange}
                        amountTestID="amount-input"
                    />

                    <View testID="give-fund-splits-section" pointerEvents={hasAmount ? 'auto' : 'none'} style={!hasAmount && styles.disabledSection}>
                        <GivingCard>
                            <FundSplitsEditor
                                testID="give-fund-splits"
                                amountCents={amountCents}
                                fundSplits={fundSplits}
                                funds={funds || []}
                                onChange={setFundSplits}
                            />
                        </GivingCard>
                    </View>

                    <RecurringGiftToggle
                        testID="give-recurring"
                        value={recurring}
                        onChange={setRecurring}
                        onManage={() => setShowRecurringModal(true)}
                    />

                    <GivingCard>
                        <PaymentMethodPicker
                            testID="give-payment-method"
                            methods={paymentMethods || []}
                            selectedPaymentMethodID={selectedPaymentMethodID}
                            onSelect={handleSelectPaymentMethod}
                            onAdd={() => setShowAddMethod(true)}
                            onManage={() => setShowManageMethods(true)}
                        />
                    </GivingCard>

                    <GivingCard>
                        <NoteInput collapsible testID="give-note" value={note} onChange={setNote} />
                    </GivingCard>

                    {paymentError && (
                        <View style={styles.errorCard} testID="give-payment-error">
                            <Text style={styles.errorText}>{paymentError}</Text>
                        </View>
                    )}

                    <View style={styles.actionArea}>
                        <SolidButton
                            testID="give-submit-button"
                            text={submitButtonText}
                            iconName="heart-outline"
                            type="giving"
                            size="lg"
                            disabled={!!givingDisabledReason}
                            processing={createPayinMutation.isPending || processing}
                            onPress={handleGive}
                        />
                        {givingDisabledReason && <Text style={styles.disabledReason}>{givingDisabledReason}</Text>}
                        <FeeCoverageToggle allowed={organization?.allowGiversToChooseCoverFees} value={coverFees} onChange={(value) => { setCoverFees(value); setReviewedCharge(null); }} disabled={createPayinMutation.isPending || processing} />
                        {feeNotice && (
                            <Text style={styles.feeNotice} testID="give-fee-notice">{feeNotice}</Text>
                        )}
                    </View>
                </View>
                <AddPaymentMethodModal
                    isOpen={showAddMethod}
                    onClose={() => setShowAddMethod(false)}
                    onPaymentMethodAdded={handlePaymentMethodAdded}
                    organizationID={organizationID}
                    personID={personID}
                    billingContact={billingContact}
                    authenticated
                />
                <ManagePaymentMethodsModal
                    isOpen={showManageMethods}
                    onClose={() => setShowManageMethods(false)}
                    methods={paymentMethods || []}
                    organizationID={organizationID}
                    personID={personID}
                />
                <ManageRecurringGiftsModal
                    isOpen={showRecurringModal}
                    onClose={() => setShowRecurringModal(false)}
                    organizationID={organizationID}
                    personID={personID}
                />
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, roundness, shadows, typography }) => ({
    container: {
        flex: 1,
        backgroundColor: colors.background,
    },
    scroll: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    paymentContent: {
        padding: spacing.md,
        gap: spacing.md,
        alignItems: 'center',
        _web: {
            maxWidth: 500,
            alignSelf: 'center',
            width: '100%',
            paddingVertical: spacing.xl,
        },
    },
    paymentHeaderTitle: {
        fontSize: typography.h1,
        fontFamily: fonts.heading,
        color: colors.text,
        textAlign: 'center',
    },
    paymentHeaderSubtitle: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
    },
    paymentWebviewCard: {
        width: '100%',
        borderRadius: roundness.md,
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
        overflow: 'hidden',
        minHeight: 500,
    },
    paymentWebview: {
        height: 500,
    },
    processingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        alignItems: 'center',
        justifyContent: 'center',
        gap: spacing.md,
        backgroundColor: colors.surface,
    },
    processingText: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    processingSubtext: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textTertiary,
    },
    content: {
        padding: spacing.md,
        paddingBottom: spacing.xxl + 60,
        _web: {
            padding: spacing.md,
            paddingBottom: spacing.xl,
        },
    },
    // Hero and form share the same centered column (max 600 on web).
    column: {
        gap: spacing.md,
        _web: {
            maxWidth: 600,
            alignSelf: 'center',
            width: '100%',
        },
    },
    actionArea: {
        gap: spacing.sm,
    },
    disabledSection: {
        opacity: 0.5,
    },
    feeNotice: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
        lineHeight: 18,
    },
    disabledReason: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.error,
        textAlign: 'center',
    },
    successContent: {
        padding: spacing.md,
        gap: spacing.lg,
        paddingBottom: spacing.xxl + 60,
        _web: {
            maxWidth: 600,
            alignSelf: 'center',
            width: '100%',
            paddingBottom: spacing.xl,
        },
    },
    successHeader: {
        alignItems: 'center',
        gap: spacing.xs,
        paddingVertical: spacing.md,
    },
    successActions: {
        gap: spacing.sm,
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
    center: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        gap: spacing.md,
        padding: spacing.xl,
    },

    unavailableTitle: {
        fontSize: typography.h2,
        fontFamily: fonts.heading,
        color: colors.text,
        textAlign: 'center',
    },
    successTitle: {
        fontSize: typography.h1,
        fontFamily: fonts.heading,
        color: colors.giving,
    },
    successBody: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
    },
    errorCard: {
        padding: spacing.md,
        borderRadius: roundness.sm,
        backgroundColor: colors.errorContainer,
    },
    errorText: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.error,
        textAlign: 'center',
    },
}));
